import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  Sparkles, Check, ArrowRight, Star, Shield,
  Headphones, BookOpen, PenTool, Mic, CreditCard,
  Clock, Crown, Users, RefreshCw, Lock,
  Timer, Award, Flame, Zap, Rocket, BadgeCheck, Target, X, Calendar,
  Gift
} from 'lucide-react';

// Kill PostHog on payment pages to prevent cross-origin iframe errors
function useDisablePosthog() {
  React.useEffect(() => {
    const ph = window.posthog;
    if (ph) {
      if (typeof ph.stopSessionRecording === 'function') ph.stopSessionRecording();
      if (typeof ph.opt_out_capturing === 'function') ph.opt_out_capturing();
    }
    // Remove the recorder script entirely
    document.querySelectorAll('script[src*="posthog-recorder"]').forEach(s => s.remove());
    return () => {
      if (ph && typeof ph.opt_in_capturing === 'function') ph.opt_in_capturing();
    };
  }, []);
}

const LEVEL_PLANS = [
  {
    id: 'level_a2',
    level: 'A2',
    name: 'DELF A2',
    subtitle: 'Elementary',
    originalPrice: 147,
    price: 147,
    minPrep: 45,
    color: 'from-emerald-500 to-teal-600',
    colorLight: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    features: [
      'Full A2 curriculum (40 exercises)',
      'Listening, Reading, Writing, Speaking',
      'AI-powered corrections & feedback',
      'Progress tracking dashboard',
      'One FREE 14-day pause',
      'Flexible extensions available anytime'
    ]
  },
  {
    id: 'level_b1',
    level: 'B1',
    name: 'DELF B1',
    subtitle: 'Intermediate',
    originalPrice: 297,
    price: 197,
    launchOffer: true,
    discount: Math.round(((297 - 197) / 297) * 100),
    color: 'from-blue-500 to-indigo-600',
    colorLight: 'bg-blue-50 text-blue-700 border-blue-200',
    minPrep: 60,
    features: [
      'Full B1 curriculum (40 exercises)',
      'Listening, Reading, Writing, Speaking',
      'AI-powered corrections & feedback',
      'Progress tracking dashboard',
      'One FREE 14-day pause',
      'Flexible extensions available anytime'
    ]
  },
  {
    id: 'level_b2',
    level: 'B2',
    name: 'DELF B2',
    subtitle: 'Upper Intermediate',
    originalPrice: 397,
    price: 247,
    launchOffer: true,
    discount: Math.round(((397 - 247) / 397) * 100),
    badge: 'Most Popular',
    color: 'from-primary to-blue-700',
    colorLight: 'bg-primary/10 text-primary border-primary/20',
    minPrep: 75,
    features: [
      'Full B2 curriculum (40 exercises)',
      'Listening, Reading, Writing, Speaking',
      'AI-powered corrections & feedback',
      'Progress tracking dashboard',
      'One FREE 14-day pause',
      'Flexible extensions available anytime'
    ]
  }
];

const SUBSCRIPTIONS = [
  {
    id: 'success_club',
    name: 'DELF Success Club',
    price: 29,
    period: '/month',
    icon: Users,
    description: 'Stay sharp after your program ends',
    features: [
      'Limited practice exercises',
      'Weekly new content',
      'Listening + speaking practice',
      'Progress tracking continuation'
    ]
  },
  {
    id: 'coaching',
    name: 'Premium Coaching',
    price: 97,
    period: '/month',
    icon: Crown,
    description: 'Personal guidance to accelerate your progress',
    requiresLevel: true,
    features: [
      'Personal feedback (speaking + writing)',
      'Coaching support & improvement guidance',
      'Detailed performance analysis',
      'Requires a purchased level'
    ]
  }
];

const EXTENSIONS = [
  { id: 'extend_30', days: 30, label: '+30 Days', prices: { A2: 29, B1: 39, B2: 49 } },
  { id: 'extend_60', days: 60, label: '+60 Days', badge: 'Best Value', prices: { A2: 49, B1: 69, B2: 89 } }
];

// ===== COUNTDOWN TIMER =====
const LAUNCH_END_KEY = 'delf_launch_end';

function getLaunchEnd() {
  const stored = localStorage.getItem(LAUNCH_END_KEY);
  if (stored) {
    const d = new Date(stored);
    if (d > new Date()) return d;
  }
  const end = new Date(Date.now() + 48 * 60 * 60 * 1000);
  localStorage.setItem(LAUNCH_END_KEY, end.toISOString());
  return end;
}

const CountdownTimer = () => {
  const [end] = useState(() => getLaunchEnd());
  const [remaining, setRemaining] = useState({});

  const calc = useCallback(() => {
    const diff = Math.max(0, end.getTime() - Date.now());
    return {
      hours: Math.floor(diff / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      expired: diff <= 0
    };
  }, [end]);

  useEffect(() => {
    setRemaining(calc());
    const t = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(t);
  }, [calc]);

  if (remaining.expired) return null;

  const Digit = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="relative w-16 sm:w-20 h-16 sm:h-20 bg-[#0a0a0a] rounded-xl flex items-center justify-center shadow-lg shadow-black/30">
        <span className="text-3xl sm:text-4xl font-bold text-white tabular-nums" style={{ fontFamily: 'Syne, monospace' }}>
          {String(value).padStart(2, '0')}
        </span>
        <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
      </div>
      <span className="text-[10px] sm:text-xs text-muted-foreground mt-1.5 uppercase tracking-wider font-medium">{label}</span>
    </div>
  );

  const Colon = () => (
    <div className="flex flex-col gap-2 pb-5">
      <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
      <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 rounded-2xl p-6 sm:p-8 mb-10 text-center shadow-xl shadow-red-500/10"
      data-testid="launch-countdown-timer"
    >
      <div className="flex items-center justify-center gap-2 mb-4">
        <Flame className="w-5 h-5 text-yellow-300" />
        <span className="text-white font-bold text-sm sm:text-base tracking-wide uppercase">Launch Offer Ends In</span>
        <Flame className="w-5 h-5 text-yellow-300" />
      </div>
      <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4">
        <Digit value={remaining.hours} label="Hours" />
        <Colon />
        <Digit value={remaining.minutes} label="Minutes" />
        <Colon />
        <Digit value={remaining.seconds} label="Seconds" />
      </div>
      <p className="text-white/80 text-xs sm:text-sm font-medium">
        Limited launch pricing — prices increase after countdown ends.
      </p>
    </motion.div>
  );
};

// ===== PAYMENT METHOD MODAL =====
const PaymentMethodModal = ({ plan, level, email, user, onClose, onPaystackPay }) => {
  const navigate = useNavigate();
  const planData = [...LEVEL_PLANS, ...SUBSCRIPTIONS, ...EXTENSIONS].find(p => p.id === plan);
  const price = planData?.price || 0;
  const planName = planData?.name || planData?.label || plan;

  const handlePaypalCreateOrder = async () => {
    const payload = { plan, level };
    if (!user && email) payload.email = email;
    // Include referral code if within 30-day window
    const refCode = localStorage.getItem('delf_ref_code');
    const refTime = parseInt(localStorage.getItem('delf_ref_time') || '0', 10);
    if (refCode && (Date.now() - refTime) < 30 * 24 * 60 * 60 * 1000) {
      payload.ref_code = refCode;
    }
    const response = await api.post('/payments/paypal/create-order', payload);
    return response.data.order_id;
  };

  const handlePaypalApprove = async (data) => {
    try {
      const response = await api.post(`/payments/paypal/capture/${data.orderID}`);
      if (response.data.payment_status === 'paid') {
        const ref = response.data.reference;
        const guest = !user ? '&guest=1' : '';
        navigate(`/payment/success?reference=${ref}&provider=paypal${guest}`);
      } else {
        toast.error('Payment capture failed. Please try again.');
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      toast.error('Payment failed. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-border"
        data-testid="payment-method-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>Choose Payment Method</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="close-payment-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 mb-5 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">{planName}</span>
          <span className="text-lg font-bold">${price}</span>
        </div>

        {/* PayPal - PRIMARY */}
        <div className="mb-3" data-testid="paypal-button-container">
          <PayPalButtons
            style={{ layout: 'vertical', color: 'gold', shape: 'pill', label: 'paypal', height: 48 }}
            createOrder={handlePaypalCreateOrder}
            onApprove={handlePaypalApprove}
            onError={(err) => {
              console.error('PayPal error:', err);
              toast.error('PayPal checkout failed. Please try again.');
            }}
          />
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 uppercase font-medium">or</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Paystack - SECONDARY */}
        <Button
          variant="outline"
          className="w-full rounded-full py-5 font-semibold border-gray-300 hover:border-primary/40 hover:bg-primary/5"
          onClick={onPaystackPay}
          data-testid="pay-with-card-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Pay with Card
        </Button>

        <p className="text-[10px] text-center text-gray-400 mt-3">
          <Lock className="w-3 h-3 inline mr-1" />
          Secure payment — Charged in your local currency at checkout.
        </p>
      </motion.div>
    </div>
  );
};

// ===== MAIN PAGE =====
const PricingPage = () => {
  useDisablePosthog();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [processing, setProcessing] = useState(null);
  const [accessInfo, setAccessInfo] = useState(null);
  const [guestEmail, setGuestEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);

  useEffect(() => {
    if (user) {
      api.get('/payments/access').then(r => setAccessInfo(r.data)).catch(() => {});
    }
  }, [user]);

  const handlePurchase = async (planId, level = '') => {
    const planType = SUBSCRIPTIONS.find(s => s.id === planId) ? 'subscription' : (planId.startsWith('extend') ? 'extension' : 'level');

    // Extensions require login
    if (!user && planType === 'extension') {
      const redirectUrl = window.location.origin + '/pricing';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    // Subscriptions use PayPal recurring billing (guests and authenticated)
    if (planType === 'subscription') {
      // Guest needs email first
      if (!user) {
        setPendingPurchase({ planId, level, isSubscription: true });
        setShowEmailInput(true);
        return;
      }
      // Authenticated user: go directly
      await startSubscription(planId);
      return;
    }

    // Guest needs to provide email first for one-time purchases
    if (!user) {
      setPendingPurchase({ planId, level });
      setShowEmailInput(true);
      return;
    }

    // Authenticated user: go straight to payment method choice
    setPendingPurchase({ planId, level });
    setShowPaymentMethod(true);
  };

  const startSubscription = async (planId, email = '') => {
    setProcessing(planId);
    try {
      const payload = {
        plan_type: planId,
        return_url: window.location.origin + `/payment/success?type=subscription&plan=${planId}`,
        cancel_url: window.location.origin + '/pricing'
      };
      if (email) payload.email = email;
      const response = await api.post('/payments/paypal/create-subscription', payload);
      if (response.data.approval_url) {
        const subId = response.data.subscription_id;
        sessionStorage.setItem('pending_subscription_id', subId);
        sessionStorage.setItem('pending_subscription_plan', planId);
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start subscription';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!guestEmail || !guestEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    // Persist referral association on backend for reliability
    const refCode = localStorage.getItem('delf_ref_code');
    const refTime = parseInt(localStorage.getItem('delf_ref_time') || '0', 10);
    if (refCode && (Date.now() - refTime) < 30 * 24 * 60 * 60 * 1000) {
      api.post('/partners/persist-ref', { ref_code: refCode, email: guestEmail }).catch(() => {});
    }
    setShowEmailInput(false);
    // If subscription, go directly to PayPal subscription flow
    if (pendingPurchase?.isSubscription) {
      startSubscription(pendingPurchase.planId, guestEmail);
      return;
    }
    // Otherwise show payment method choice for one-time purchases
    setShowPaymentMethod(true);
  };

  const handlePaystackPay = async () => {
    if (!pendingPurchase) return;
    setShowPaymentMethod(false);
    setProcessing(pendingPurchase.planId);
    try {
      const payload = { plan: pendingPurchase.planId, level: pendingPurchase.level, origin_url: window.location.origin };
      if (!user && guestEmail) payload.email = guestEmail;
      const response = await api.post('/payments/initialize', payload);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const closeModals = () => {
    setShowEmailInput(false);
    setShowPaymentMethod(false);
    setPendingPurchase(null);
    setGuestEmail('');
  };

  const getLevelStatus = (level) => {
    if (!accessInfo) return null;
    return accessInfo.levels?.[level];
  };

  return (
    <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: 'USD' }}>
      <div className="min-h-screen bg-[#FDFCF8]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#FDFCF8]/80 backdrop-blur-md border-b border-border/50">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="pricing-logo-link">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                DELF Success Academy
              </span>
            </Link>
            <Link to="/levels">
              <Button variant="ghost" data-testid="back-to-levels-btn">Back to Levels</Button>
            </Link>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6 py-12">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
            data-testid="pricing-hero"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/10 text-red-600 text-sm font-bold mb-5">
              <Flame className="w-4 h-4" />
              Limited Launch Pricing — Save Up To 38%
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Choose Your <span className="text-primary">DELF Success</span> Plan
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Pass your DELF exam faster with a structured, AI-powered system designed for real results.
            </p>
          </motion.div>

          {/* === COUNTDOWN TIMER === */}
          <CountdownTimer />

          {/* === GUEST EMAIL MODAL === */}
          <AnimatePresence>
            {showEmailInput && (
              <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => { setShowEmailInput(false); setPendingPurchase(null); }}>
                <motion.form
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  onClick={e => e.stopPropagation()}
                  onSubmit={handleEmailSubmit}
                  className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-border"
                  data-testid="guest-email-modal"
                >
                  <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Enter your email</h3>
                  <p className="text-sm text-muted-foreground mb-4">We'll link your purchase to this email after payment.</p>
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={e => setGuestEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 mb-4"
                    autoFocus
                    data-testid="guest-email-input"
                  />
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => { setShowEmailInput(false); setPendingPurchase(null); }}>Cancel</Button>
                    <Button type="submit" className="flex-1 rounded-full" data-testid="guest-email-submit">
                      Continue to Payment
                    </Button>
                  </div>
                </motion.form>
              </div>
            )}
          </AnimatePresence>

          {/* === PAYMENT METHOD MODAL === */}
          <AnimatePresence>
            {showPaymentMethod && pendingPurchase && (
              <PaymentMethodModal
                plan={pendingPurchase.planId}
                level={pendingPurchase.level}
                email={guestEmail}
                user={user}
                onClose={closeModals}
                onPaystackPay={handlePaystackPay}
              />
            )}
          </AnimatePresence>

          {/* === LEVEL PROGRAMS === */}
          <section className="mb-12" data-testid="level-programs-section">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>One-Time Programs</h2>
                <p className="text-sm text-muted-foreground">Full access with time-bound study periods</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {LEVEL_PLANS.map((plan, index) => {
                const status = getLevelStatus(plan.level);
                const isActive = status?.active;
                const isExpired = status?.expired;
                const hasDiscount = plan.launchOffer && plan.originalPrice > plan.price;

                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * (index + 1) }}
                    data-testid={`level-card-${plan.level.toLowerCase()}`}
                    className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all ${
                      plan.badge ? 'border-primary shadow-xl shadow-primary/10 scale-[1.02]' : 'border-border/50 hover:border-primary/30'
                    }`}
                  >
                    {plan.badge && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-primary to-blue-700 text-white text-center py-2 text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-1.5" data-testid="most-popular-badge">
                        <Flame className="w-3.5 h-3.5" />
                        {plan.badge} — Launch Offer
                      </div>
                    )}

                    {plan.launchOffer && !plan.badge && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-red-500 to-orange-500 text-white text-center py-2 text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-1.5" data-testid="launch-offer-badge">
                        <Flame className="w-3.5 h-3.5" />
                        Launch Offer
                      </div>
                    )}

                    <div className={`p-6 ${plan.badge || plan.launchOffer ? 'pt-12' : ''}`}>
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${plan.colorLight} mb-4`}>
                        <BookOpen className="w-3 h-3" />
                        {plan.subtitle}
                      </div>

                      <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{plan.name}</h3>

                      <div className="flex items-baseline gap-2 mb-1">
                        {hasDiscount && (
                          <span className="text-xl text-muted-foreground line-through" data-testid={`original-price-${plan.level.toLowerCase()}`}>
                            ${plan.originalPrice}
                          </span>
                        )}
                        <span className={`text-4xl font-bold ${hasDiscount ? 'text-red-600' : ''}`} data-testid={`price-${plan.level.toLowerCase()}`}>
                          ${plan.price}
                        </span>
                      </div>

                      {hasDiscount && (
                        <div className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full mb-2" data-testid={`discount-badge-${plan.level.toLowerCase()}`}>
                          <Zap className="w-3 h-3" />
                          Save ${plan.originalPrice - plan.price} ({plan.discount}% off)
                        </div>
                      )}

                      <div className="mb-6">
                        <p className="text-xs text-muted-foreground leading-relaxed mb-2 flex items-start gap-1.5">
                          <Calendar className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          Personalized exam preparation access based on your official DELF exam date.
                        </p>
                        <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Minimum preparation: {plan.minPrep} days
                        </p>
                      </div>

                      <ul className="space-y-2.5 mb-6">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      {isActive ? (
                        <div className="space-y-2">
                          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-center">
                            <p className="text-sm font-semibold text-green-700">Active</p>
                            <p className="text-xs text-green-600">{status.days_remaining} days remaining</p>
                          </div>
                          <Button variant="outline" className="w-full rounded-full" onClick={() => navigate(`/level/${plan.level}`)} data-testid={`go-to-level-${plan.level.toLowerCase()}-btn`}>
                            Go to {plan.level} <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      ) : isExpired ? (
                        <div className="space-y-2">
                          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-center">
                            <p className="text-xs font-semibold text-amber-700">Access Expired</p>
                          </div>
                          <Button
                            className={`w-full rounded-full py-5 font-bold bg-gradient-to-r ${plan.color} text-white border-0`}
                            onClick={() => handlePurchase(plan.id, plan.level)}
                            disabled={processing === plan.id}
                            data-testid={`repurchase-${plan.level.toLowerCase()}-btn`}
                          >
                            {processing === plan.id ? 'Processing...' : `Repurchase — $${plan.price}`}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          className={`w-full rounded-full py-5 font-bold text-[15px] ${
                            plan.badge
                              ? 'bg-gradient-to-r from-primary to-blue-700 text-white border-0 shadow-lg shadow-primary/25'
                              : hasDiscount
                              ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white border-0 shadow-lg shadow-red-500/20'
                              : ''
                          }`}
                          onClick={() => handlePurchase(plan.id, plan.level)}
                          disabled={processing === plan.id}
                          data-testid={`purchase-${plan.level.toLowerCase()}-btn`}
                        >
                          {processing === plan.id ? 'Processing...' : (
                            <>
                              <Rocket className="w-4 h-4 mr-2" />
                              Start My DELF Success Now — ${plan.price}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* === EXCLUSIVE LAUNCH BONUS === */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-12"
            data-testid="exclusive-bonus-section"
          >
            <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-8 md:p-10 shadow-lg shadow-amber-100/50">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400 text-gray-900 text-sm font-bold mb-4 shadow-sm">
                  <Gift className="w-4 h-4" />
                  EXCLUSIVE LAUNCH BONUS
                </div>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  FREE Private DELF Strategy Session with Tony Eboulondzi
                </h3>
                <p className="text-lg font-semibold text-amber-700">(Value: $250)</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Available ONLY for the first 10 enrollments per DELF level.
                </p>
              </div>

              {/* Session Durations */}
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                {[
                  { level: 'A2', duration: '50-minute', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
                  { level: 'B1', duration: '80-minute', color: 'bg-blue-100 text-blue-800 border-blue-300' },
                  { level: 'B2', duration: '120-minute', color: 'bg-purple-100 text-purple-800 border-purple-300' }
                ].map((item) => (
                  <div key={item.level} className={`rounded-xl border p-4 text-center ${item.color}`}>
                    <p className="font-bold text-lg">{item.level}</p>
                    <p className="text-sm font-medium">{item.duration} private coaching session</p>
                  </div>
                ))}
              </div>

              {/* What's Covered */}
              <div className="bg-white/70 rounded-xl p-6 mb-8 border border-amber-200/60">
                <p className="font-semibold text-sm mb-3">During your private session we will:</p>
                <ul className="space-y-2">
                  {[
                    'Review all four DELF modules',
                    'Identify your biggest weaknesses',
                    'Practice the exact skills you need most',
                    'Share proven examiner tips and strategies',
                    'Build your personalized study plan for the remaining days before your exam'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* How to Claim */}
              <div className="text-center mb-8">
                <p className="font-semibold text-sm mb-3">How to Claim</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm">
                  <span className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-amber-400 text-gray-900 text-xs font-bold flex items-center justify-center">1</span> Register today</span>
                  <ArrowRight className="w-4 h-4 text-amber-400 hidden sm:block" />
                  <span className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-amber-400 text-gray-900 text-xs font-bold flex items-center justify-center">2</span> Schedule your session</span>
                  <ArrowRight className="w-4 h-4 text-amber-400 hidden sm:block" />
                  <span className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-amber-400 text-gray-900 text-xs font-bold flex items-center justify-center">3</span> Increase your confidence</span>
                </div>
              </div>

              {/* Urgency Box */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center" data-testid="bonus-urgency-box">
                <p className="text-sm font-bold text-red-700">
                  ⚠️ Only 10 students per DELF level will receive this exclusive bonus.
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Once all places are taken, this bonus will disappear permanently.
                </p>
              </div>
            </div>
          </motion.section>

          {/* === GUARANTEE === */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-12 text-center"
            data-testid="guarantee-section"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-green-600" />
              <h3 className="font-bold text-green-800" style={{ fontFamily: 'Syne, sans-serif' }}>7-Day Satisfaction Guarantee</h3>
            </div>
            <p className="text-sm text-green-700 max-w-lg mx-auto">
              Try the system risk-free. If you're not satisfied, get your money back. No questions asked.
            </p>
          </motion.div>

          {/* === EXTEND ACCESS === */}
          {accessInfo && Object.values(accessInfo.levels || {}).some(l => l.purchased) && (
            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-16" data-testid="extend-access-section">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Extend Your Access</h2>
                  <p className="text-sm text-muted-foreground">Need more time? Extend any level you've purchased</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-border/50 p-6">
                {['A2', 'B1', 'B2'].map(level => {
                  const status = getLevelStatus(level);
                  if (!status?.purchased) return null;
                  return (
                    <div key={level} className="flex flex-col sm:flex-row sm:items-center justify-between py-4 border-b border-border/30 last:border-0" data-testid={`extend-row-${level.toLowerCase()}`}>
                      <div className="mb-3 sm:mb-0">
                        <p className="font-semibold">DELF {level}</p>
                        <p className="text-sm text-muted-foreground">
                          {status.active ? <span className="text-green-600">{status.days_remaining} days remaining</span> : <span className="text-amber-600">Expired — extend to regain access</span>}
                        </p>
                      </div>
                      <div className="flex gap-3">
                        {EXTENSIONS.map(ext => (
                          <Button key={ext.id} variant="outline" size="sm" className="rounded-full relative" onClick={() => handlePurchase(ext.id, level)} disabled={processing === `${ext.id}_${level}`} data-testid={`extend-${ext.id}-${level.toLowerCase()}-btn`}>
                            {ext.badge && <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{ext.badge}</span>}
                            {ext.label} — ${ext.prices[level]}
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.section>
          )}

          {/* === CRAZY LAUNCH DEAL === */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-16" data-testid="coaching-deal-section">
            <div className="bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 border-2 border-red-300 rounded-2xl p-8 md:p-10 shadow-lg shadow-red-100/50">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-500 text-white text-sm font-bold mb-4 shadow-sm">
                  <Flame className="w-4 h-4" />
                  CRAZY LAUNCH DEAL
                </div>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Premium 1-on-1 Private DELF Coaching with Tony Eboulondzi
                </h3>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 text-gray-700 text-xs font-semibold border border-gray-200 shadow-sm">
                  <Award className="w-3.5 h-3.5 text-amber-500" />
                  25+ Years of DELF Exam Expertise
                </div>
              </div>

              {/* Package Includes */}
              <div className="bg-white/70 rounded-xl p-6 mb-6 border border-red-200/60">
                <p className="font-semibold text-sm mb-4">Package Includes:</p>
                <ul className="space-y-2.5">
                  {[
                    { text: '20 private coaching lessons', sub: '(1 hour each)' },
                    { text: 'Complete preparation for all four DELF modules' },
                    { text: 'Proven examiner tips and strategies' },
                    { text: 'Personal feedback on your Writing and Speaking' },
                    { text: 'Answers to all your DELF questions' },
                    { text: 'Detailed performance analysis' },
                    { text: 'Personalized coaching tailored to your weaknesses' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                      <span>{item.text} {item.sub && <span className="text-muted-foreground">{item.sub}</span>}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Outcome */}
              <div className="bg-white/70 rounded-xl p-6 mb-6 border border-red-200/60">
                <p className="font-semibold text-sm mb-3">At the end of our coaching together you will:</p>
                <ul className="space-y-2">
                  {[
                    'Feel fully prepared',
                    'Walk into your DELF examination with confidence',
                    'Maximize your chances of passing'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Crazy Bonus */}
              <div className="bg-amber-100/80 border-2 border-amber-400 rounded-xl p-6 mb-8 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400 text-gray-900 text-sm font-bold mb-3">
                  <Gift className="w-4 h-4" />
                  CRAZY BONUS
                </div>
                <p className="text-sm font-semibold mb-2">Only for the FIRST 5 students.</p>
                <p className="text-sm mb-1">Register today and receive</p>
                <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>3 EXTRA Private Lessons</p>
                <p className="text-sm text-muted-foreground mb-2">(1 hour each) — FREE.</p>
                <p className="text-lg font-bold text-primary">Total: 23 private coaching lessons.</p>
              </div>

              {/* Price + CTA */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1 uppercase tracking-wider font-medium">One-Time Investment</p>
                <p className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>$497</p>
                <Button
                  size="lg"
                  className="rounded-full px-12 py-7 font-bold text-base bg-gradient-to-r from-red-500 to-orange-500 text-white border-0 shadow-xl shadow-red-500/25 hover:shadow-red-500/40 transition-shadow"
                  onClick={() => handlePurchase('coaching_package')}
                  disabled={processing === 'coaching_package'}
                  data-testid="claim-coaching-btn"
                >
                  {processing === 'coaching_package' ? 'Processing...' : (
                    <>
                      <Rocket className="w-5 h-5 mr-2" />
                      Claim My Coaching Package
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.section>

          {/* === WHAT'S INCLUDED === */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mb-12" data-testid="whats-included-section">
            <h2 className="text-base md:text-lg font-bold text-center mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>What's Inside Every Level</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { icon: Headphones, title: 'Listening', desc: '10 audio exercises with traps & strategies', bg: 'bg-sky-50', ic: 'text-sky-600' },
                { icon: BookOpen, title: 'Reading', desc: '10 comprehension exercises with explanations', bg: 'bg-emerald-50', ic: 'text-emerald-600' },
                { icon: PenTool, title: 'Writing', desc: '10 tasks with AI grading & model answers', bg: 'bg-amber-50', ic: 'text-amber-600' },
                { icon: Mic, title: 'Speaking', desc: '10 prompts with STT + AI evaluation', bg: 'bg-rose-50', ic: 'text-rose-600' }
              ].map((mod, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-border/50 text-center">
                  <div className={`w-11 h-11 rounded-xl ${mod.bg} flex items-center justify-center mx-auto mb-3`}>
                    <mod.icon className={`w-5 h-5 ${mod.ic}`} />
                  </div>
                  <h4 className="font-bold text-sm mb-1">{mod.title}</h4>
                  <p className="text-xs text-muted-foreground">{mod.desc}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* === STUDENT REVIEWS === */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="bg-white border border-border/50 rounded-2xl p-6 mb-8" data-testid="trust-triggers-section">
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { quote: 'I passed DELF B2 with confidence thanks to the structured practice.', name: 'Sarah M.', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face' },
                { quote: 'The speaking corrections completely changed my confidence.', name: 'Marco L.', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face' },
                { quote: 'The closest preparation I\u2019ve ever seen to the real DELF exam.', name: 'Anna K.', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face' }
              ].map((r, i) => (
                <div key={i} className="text-center">
                  <img
                    src={r.photo}
                    alt={r.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-border shadow-sm mx-auto mb-3"
                    loading="lazy"
                  />
                  <div className="flex items-center justify-center gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700 italic mb-2">&ldquo;{r.quote}&rdquo;</p>
                  <p className="text-xs font-semibold text-muted-foreground">&mdash; {r.name}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Trust Bar */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="text-center pb-8" data-testid="trust-bar">
            <div className="inline-flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Secure Payment</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4" /> 7-Day Money-Back</span>
              <span className="flex items-center gap-1.5"><Lock className="w-4 h-4" /> Instant Access</span>
            </div>
            <p className="text-sm text-muted-foreground mt-4 mb-1 font-semibold text-foreground">Need Help Fast?</p>
            <p className="text-sm text-muted-foreground mb-3">Contact us instantly on WhatsApp.</p>
            <a
              href="https://wa.me/message/OLMYDM6IORLZF1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] hover:bg-[#1fb855] text-white font-semibold text-sm shadow-md transition-colors"
              data-testid="pricing-whatsapp-btn"
            >
              💬 Chat with Us on WhatsApp
            </a>
            <p className="text-sm text-muted-foreground mt-6">
              &copy; 2026 DELF Success Academy. All rights reserved.
            </p>
          </motion.div>
        </main>
      </div>
    </PayPalScriptProvider>
  );
};

export default PricingPage;
